/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { apiClient } from '@lib/api-client';
import { queryClient } from '@lib/react-query';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';

// I18n
import { useTranslation } from 'react-i18next';

// Types
import { TApiResponse } from '@features/resources/measurabilities/types';
import { IApiError, TParams } from '@global-types/index';
import { TBodyApi } from 'shared/types';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const addMeasurabilityProject = (
	data: TBodyApi
): Promise<{
	response: TApiResponse;
}> => {
	return apiClient.post('/measurabilities', data);
};

export const useAddMeasurabilityProject = (
	page: number,
	size: number,
	queryParams: TParams,
	sortParams: string
) => {
	const { t } = useTranslation();
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: addMeasurabilityProject,

		onSuccess: async () => {
			notifications.show({
				title: t('global.success'),
				message: t(
					'measurabilities.notifications.measurabilitiesSuccessfullyAdded'
				),
				color: 'var(--mantine-color-igluGreen-9)',
			});
			queryClient.invalidateQueries({
				queryKey: [
					'databases-measurabilities',
					{
						page: page,
						size: size,
						queryParams: queryParams,
						sortParams: sortParams,
					},
				],
			});
		},
		onError: (error: IApiError) => {
			notifications.show({
				title: t('global.error'),
				message: t(`api.${error.type}`),
				color: 'var(--mantine-color-igluRed-9)',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
